import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BadgeCheck,
  ChevronRight,
  Clock,
  Download,
  EyeOff,
  HardDrive,
  Heart,
  Play,
  ShieldAlert,
  Star,
  Tag,
} from 'lucide-react';
import MapleMarketAppCard from '@/components/maple-market/MapleMarketAppCard';
import MapleMarketFooter from '@/components/maple-market/MapleMarketFooter';
import MapleMarketNavbar, { MAPLE_MARKET_NAV_TOTAL_HEIGHT } from '@/components/maple-market/MapleMarketNavbar';
import MarketplacePurchaseDialog from '@/components/maple-market/MarketplacePurchaseDialog';
import { formatMapleMarketPrice, type MapleMarketCategory, type MapleOSEditionSku } from '@/data/mapleMarketApps';
import type { MapleMarketInfoPageId } from '@/components/maple-market/MapleMarketInfoView';
import { useMapleMarketLicenses } from '@/hooks/supabase/useMapleMarketLicenses';
import { useMarketplaceProducts } from '@/hooks/supabase/useMarketplaceProducts';
import { useMarketplaceWishlist } from '@/hooks/supabase/useMarketplaceWishlist';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useMapleMarketInstaller } from '@/OS/utils/MapleMarketInstaller';
import { useOsLicense } from '@/hooks/supabase/useOsLicense';

interface MapleMarketDetailViewProps {
  appId: string;
  embedded?: boolean;
  isAdminAvailable?: boolean;
  onOpenAdmin?: () => void;
  onOpenLibrary?: () => void;
  onSelectCategory: (category: MapleMarketCategory | 'All') => void;
  onSelectApp: (appId: string) => void;
  onOpenPage?: (page: MapleMarketInfoPageId) => void;
}

const INSTALL_STAGE_LABELS: Record<string, string> = {
  requesting_download: 'Requesting download',
  downloading_package: 'Downloading package',
  validating_manifest: 'Validating package',
  installing_files: 'Installing to Program Files',
  recording_install: 'Recording install',
  launching_app: 'Launching app',
  ready: 'Ready to run',
};

const MAPLE_OS_EDITION_LABELS: Record<string, string> = {
  'mapleos-26': 'MapleOS 26',
  'mapleos-pro-26': 'MapleOS Pro 26',
  'mapleos-enterprise-26': 'MapleOS Enterprise 26',
};

function getVisibilityLabel(status: 'hidden' | 'unpublished' | 'published') {
  if (status === 'published') {
    return 'Published';
  }

  return status === 'hidden' ? 'Hidden' : 'Unpublished';
}

export default function MapleMarketDetailView({
  appId,
  embedded = false,
  isAdminAvailable = false,
  onOpenAdmin,
  onOpenLibrary,
  onSelectCategory,
  onSelectApp,
  onOpenPage,
}: MapleMarketDetailViewProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { products, storeProducts, getProductById } = useMarketplaceProducts();
  const app = getProductById(appId);
  const { sku: osLicenseSku } = useOsLicense();
  const relatedApps = useMemo(() => {
    if (!app) {
      return [];
    }

    return storeProducts
      .filter((relatedApp) => relatedApp.category === app.category && relatedApp.id !== app.id)
      .slice(0, 4);
  }, [app, storeProducts]);
  const licenseSkus = useMemo(
    () => Array.from(new Set((app ? [app.sku, ...relatedApps.map((relatedApp) => relatedApp.sku)] : []))),
    [app, relatedApps]
  );
  const ownedSkus = useMapleMarketLicenses(licenseSkus);
  const { wishlistedSkus, toggleWishlist } = useMarketplaceWishlist(app ? [app.sku] : []);
  const { installProduct } = useMapleMarketInstaller();
  const [installStage, setInstallStage] = useState<string | null>(null);
  const [installing, setInstalling] = useState(false);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchasedInSession, setPurchasedInSession] = useState(false);

  if (!app) {
    return (
      <div className={embedded ? 'min-h-full bg-background' : 'min-h-screen bg-background'}>
        <MapleMarketNavbar
          products={storeProducts}
          embedded={embedded}
          isAdminAvailable={isAdminAvailable}
          onOpenAdmin={onOpenAdmin}
          onOpenLibrary={onOpenLibrary}
          onSelectCategory={onSelectCategory}
          onSelectApp={onSelectApp}
        />
        <main
          className={`container text-center ${embedded ? 'pt-6' : ''}`}
          style={embedded ? undefined : { paddingTop: `calc(${MAPLE_MARKET_NAV_TOTAL_HEIGHT} + 3rem)` }}
        >
          <div className="mx-auto max-w-xl rounded-2xl border border-border bg-card p-8 shadow-card">
            <ShieldAlert className="mx-auto mb-4 h-10 w-10 text-primary" />
            <h1 className="mb-3 text-2xl font-bold text-foreground">This product is not available</h1>
            <p className="mb-5 text-sm text-muted-foreground">
              Hidden and unpublished Maple Market listings are only available to site admins, product members, and users who already own the product.
            </p>
            <button type="button" onClick={() => onSelectCategory('All')} className="text-primary hover:underline">
              Back to Store
            </button>
          </div>
        </main>
      </div>
    );
  }

  const stars = Array.from({ length: 5 }, (_, index) => index < Math.round(app.rating));
  const isOwned = !app.isFree && (ownedSkus.has(app.sku) || purchasedInSession);
  const isWishlisted = wishlistedSkus.has(app.sku);
  const canInstallPackage = app.packageType !== 'none' && !!app.packagePath;
  const currentEdition: MapleOSEditionSku = osLicenseSku === 'mapleos-enterprise-26'
    ? 'mapleos-enterprise-26'
    : osLicenseSku === 'mapleos-pro-26'
      ? 'mapleos-pro-26'
      : 'mapleos-26';
  const installAllowedOnCurrentEdition = app.supportedMapleOSEditions.includes(currentEdition);
  const installWouldRunNow = app.isFree || isOwned;
  const supportedEditionLabels = app.supportedMapleOSEditions.map((edition) => MAPLE_OS_EDITION_LABELS[edition] ?? edition);
  const packageAvailabilityLabel = app.packageType === 'package' ? '.mpak package available' : 'HTML bundle available';
  const primaryLabel = installing
    ? INSTALL_STAGE_LABELS[installStage || 'requesting_download'] || 'Installing'
    : installWouldRunNow && !installAllowedOnCurrentEdition
      ? `Requires ${supportedEditionLabels.join(' or ')}`
      : canInstallPackage
        ? app.isFree || isOwned
          ? 'Install'
          : 'Buy & Install'
        : app.isFree || isOwned
          ? 'Package coming soon'
          : 'Buy License';
  const contentSpacing = embedded ? 'pt-6' : undefined;

  const requireAuth = () => {
    if (user) {
      return true;
    }

    if (embedded) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in to buy, install, or save marketplace items.',
        variant: 'destructive',
      });
      return false;
    }

    navigate('/auth');
    return false;
  };

  const handleInstall = async () => {
    if (!requireAuth()) {
      return;
    }

    if (!canInstallPackage) {
      toast({
        title: 'Package coming soon',
        description: 'This product license is available now, but its install package has not been published yet.',
      });
      return;
    }

    try {
      setInstalling(true);
      setInstallStage('requesting_download');

      const { data, error } = await supabase.functions.invoke('request-marketplace-download', {
        body: { sku: app.sku },
      });

      if (error || !data?.url || !data?.version) {
        throw new Error(error?.message || 'Unable to request signed package download');
      }

      await installProduct({
        product: app,
        signedUrl: data.url,
        version: data.version,
        onProgress: setInstallStage,
      });

      toast({
        title: 'Installed successfully',
        description: `${app.name} is ready to run inside MapleOS.`,
      });
    } catch (error) {
      toast({
        title: 'Install failed',
        description: error instanceof Error ? error.message : 'Unable to install this marketplace package.',
        variant: 'destructive',
      });
    } finally {
      setInstalling(false);
      setInstallStage((current) => (current === 'ready' ? current : null));
    }
  };

  const handleCheckout = () => {
    if (!requireAuth()) {
      return;
    }

    setPurchaseDialogOpen(true);
  };

  const handlePrimaryAction = async () => {
    if (installing) {
      return;
    }

    if ((app.isFree || isOwned) && !installAllowedOnCurrentEdition) {
      toast({
        title: 'MapleOS edition not supported',
        description: `This app can only be installed on ${supportedEditionLabels.join(', ')}.`,
        variant: 'destructive',
      });
      return;
    }

    if (app.isFree || isOwned) {
      await handleInstall();
      return;
    }

    handleCheckout();
  };

  const handleWishlistToggle = async () => {
    if (!requireAuth()) {
      return;
    }

    try {
      const next = await toggleWishlist(app.sku);
      toast({
        title: next ? 'Added to wishlist' : 'Removed from wishlist',
        description: next ? `${app.name} has been saved for later.` : `${app.name} is no longer in your wishlist.`,
      });
    } catch (error) {
      toast({
        title: 'Wishlist update failed',
        description: error instanceof Error ? error.message : 'Unable to update your wishlist.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className={embedded ? 'min-h-full bg-background' : 'min-h-screen bg-background'}>
      <MapleMarketNavbar
        products={storeProducts}
        embedded={embedded}
        isAdminAvailable={isAdminAvailable}
        onOpenAdmin={onOpenAdmin}
        onOpenLibrary={onOpenLibrary}
        onSelectCategory={onSelectCategory}
        onSelectApp={onSelectApp}
      />
      <main
        className={`container pb-8 ${contentSpacing ?? ''}`}
        style={embedded ? undefined : { paddingTop: `calc(${MAPLE_MARKET_NAV_TOTAL_HEIGHT} + 1.5rem)` }}
      >
        <div className="mb-6 flex items-center gap-2 text-sm text-muted-foreground animate-fade-in">
          <button type="button" onClick={() => onSelectCategory('All')} className="transition-colors hover:text-foreground">
            Store
          </button>
          <ChevronRight className="h-3.5 w-3.5" />
          <button type="button" onClick={() => onSelectCategory(app.category)} className="transition-colors hover:text-foreground">
            {app.category}
          </button>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-foreground">{app.name}</span>
        </div>

        <div className="mb-12 grid grid-cols-1 gap-8 animate-fade-in lg:grid-cols-5" style={{ animationDelay: '80ms' }}>
          <div className="lg:col-span-3">
            <div className="overflow-hidden rounded-xl border border-border shadow-card">
              <img src={app.image} alt={app.name} width={768} height={512} className="aspect-video w-full object-cover" />
            </div>
          </div>

          <div className="flex flex-col lg:col-span-2">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {isOwned && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                  <BadgeCheck className="h-3.5 w-3.5" />
                  Owned
                </span>
              )}
              {app.visibilityStatus !== 'published' && (
                <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                  <EyeOff className="h-3.5 w-3.5" />
                  {getVisibilityLabel(app.visibilityStatus)}
                </span>
              )}
              {app.publishRequestStatus === 'pending' && (
                <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                  Publish request pending
                </span>
              )}
              {app.packageType !== 'none' && (
                <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                  {packageAvailabilityLabel}
                </span>
              )}
              <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                Install on {supportedEditionLabels.join(', ')}
              </span>
            </div>
            <h1 className="mb-1 text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
              {app.name}
            </h1>
            <p className="mb-4 text-sm text-muted-foreground">{app.developer}</p>

            <div className="mb-4 flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                {stars.map((filled, index) => (
                  <Star
                    key={index}
                    className={`h-4 w-4 ${filled ? 'fill-primary text-primary' : 'text-muted-foreground/30'}`}
                  />
                ))}
              </div>
              <span className="text-sm font-medium text-foreground">{app.rating.toFixed(1)}</span>
              <span className="text-sm text-muted-foreground">({app.reviews.toLocaleString()} reviews)</span>
            </div>

            <div className="mb-5 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                <Tag className="h-4 w-4" />
                {app.category}
              </span>
              <span className="inline-flex items-center gap-2">
                <HardDrive className="h-4 w-4" />
                {app.size}
              </span>
              <span className="inline-flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Released {new Date(app.releaseDate).toLocaleDateString()}
              </span>
            </div>

            {app.publishRejectionReason && (
              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <div className="font-medium">Latest publish review</div>
                <div className="mt-1">{app.publishRejectionReason}</div>
              </div>
            )}

            <p className="mb-6 text-sm leading-6 text-muted-foreground">{app.description}</p>

            <div className="mb-4 rounded-xl border border-border bg-card p-5 shadow-card">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <div className="text-3xl font-bold text-foreground">{formatMapleMarketPrice(app.price)}</div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {app.isFree ? 'Install instantly inside MapleOS' : 'One-time purchase license'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => void handleWishlistToggle()}
                  className={`inline-flex h-11 w-11 items-center justify-center rounded-xl border transition-colors ${
                    isWishlisted
                      ? 'border-primary/40 bg-primary/10 text-primary'
                      : 'border-border bg-background text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Heart className={`h-5 w-5 ${isWishlisted ? 'fill-current' : ''}`} />
                </button>
              </div>
              <button
                type="button"
                disabled={installing || (installWouldRunNow && !installAllowedOnCurrentEdition)}
                onClick={() => void handlePrimaryAction()}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {app.isFree || isOwned ? <Download className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                {primaryLabel}
              </button>

              {!installAllowedOnCurrentEdition && user && (
                <p className="mt-3 text-sm text-muted-foreground">
                  Your current MapleOS edition is {MAPLE_OS_EDITION_LABELS[currentEdition]}. This app is limited to {supportedEditionLabels.join(', ')}.
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {app.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        <section className="mb-12 animate-fade-in" style={{ animationDelay: '150ms' }}>
          <h2 className="mb-4 text-xl font-bold text-foreground">About this app</h2>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <p className="whitespace-pre-line text-sm leading-7 text-muted-foreground">{app.longDescription}</p>
          </div>
        </section>

        {relatedApps.length > 0 && (
          <section className="animate-fade-in" style={{ animationDelay: '220ms' }}>
            <h2 className="mb-5 text-xl font-bold text-foreground">You may also like</h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
              {relatedApps.map((relatedApp) => (
                <MapleMarketAppCard
                  key={relatedApp.id}
                  app={relatedApp}
                  owned={ownedSkus.has(relatedApp.sku)}
                  onSelectApp={onSelectApp}
                />
              ))}
            </div>
          </section>
        )}
      </main>
      <MapleMarketFooter onSelectCategory={onSelectCategory} onOpenPage={onOpenPage} />
      <MarketplacePurchaseDialog
        open={purchaseDialogOpen}
        onOpenChange={setPurchaseDialogOpen}
        product={app}
        onSuccess={async () => {
          setPurchasedInSession(true);
          if (canInstallPackage) {
            await handleInstall();
          }
        }}
      />
    </div>
  );
}